import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { useOffersTable } from '../../views/Offers/queries';
import ZuulLoader from '../../components/ZuulLoader/ZuulLoader';
import DynamicList from '../../components/Virtualization/DynamicList';
import { PaneHeading } from './VariablePane';

interface Offer {
  id: number | string;
  name?: string;
  advertiser?: { name?: string };
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: Offer[];
}

const OfferItem = React.memo(function OfferItem({ index, style, data }: RowProps) {
  const offer = data[index];
  if (!offer) return null;
  return (
    <Box style={style} sx={{ px: 1.5, py: 1, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Link
          component={RouterLink}
          to={`/campaigns/offertable/offer/${offer.id}`}
          underline='hover'
          variant='body2'
          noWrap
          sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
        >
          {offer.name}
        </Link>
        <Chip size='small' color='primary' label={offer.id} />
      </Box>
      {offer.advertiser?.name && (
        <Chip size='small' variant='outlined' label={offer.advertiser.name} sx={{ mt: 0.75, maxWidth: '100%' }} />
      )}
    </Box>
  );
});

/** Searchable, virtualized list of offers for the side panel. */
export default function OffersPane() {
  const { data, isLoading, error } = useOffersTable() as { data?: unknown; isLoading: boolean; error?: unknown };
  const offers = Array.isArray(data) ? (data as Offer[]) : null;
  const [term, setTerm] = React.useState('');

  const filtered = React.useMemo(() => {
    const list = offers ?? [];
    const query = term.trim().toLowerCase();
    if (!query) return list;
    return list.filter(
      (offer) => (offer.name?.toLowerCase() ?? '').includes(query) || String(offer.id ?? '').includes(query)
    );
  }, [offers, term]);

  if (isLoading) return <ZuulLoader text='Loading Offers...' />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {error ? (
        <Typography variant='body2' color='error' sx={{ p: 1.5 }}>
          Error loading offers: {JSON.stringify(error)}
        </Typography>
      ) : null}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <PaneHeading>Offers ({filtered.length})</PaneHeading>
        <TextField
          size='small'
          fullWidth
          name='offersSearch'
          placeholder='Search by offer name or ID...'
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'Search offers' },
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {filtered.length > 0 ? (
          <DynamicList items={filtered} rowHeight={80}>
            {(props: RowProps) => <OfferItem {...props} />}
          </DynamicList>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
            {!offers ? 'No offers data available' : 'No offers found'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
